import { request } from '@/api/client';
import type {
  SSOCallbackRequest,
  SSOCallbackResult,
  SSOFlow,
  SSOLinkCode,
  SSOLinkResult,
  SSOProvider,
} from '@/types/sso';

const BASE = '/v1/account/sso';

// the pages show SSO failures inline, keyed by the error code
const QUIET_OPTIONS = { skipDefaultErrorHandler: true };

/**
 * The providers this deployment offers; an empty list means SSO is switched off.
 */
export function fetchSSOProviders(): Promise<SSOProvider[]> {
  return request('GET', `${BASE}/providers`, {}, undefined, QUIET_OPTIONS).then((response) =>
    ((response.data ?? []) as { key: SSOProvider }[]).map((provider) => provider.key)
  );
}

/**
 * Starts a sign-in on the session and returns the provider URL to navigate to.
 */
export function startSSO(provider: SSOProvider, flow: SSOFlow, returnPath?: string): Promise<string> {
  return request(
    'POST',
    `${BASE}/${provider}/start`,
    {},
    { flow, ...(returnPath ? { return_path: returnPath } : {}) },
    QUIET_OPTIONS
  ).then((response) => response.data.authorization_url as string);
}

/**
 * Hands the provider's redirect parameters to the backend, which verifies them and signs in or prepares a registration.
 */
export function completeSSO(provider: SSOProvider, body: SSOCallbackRequest): Promise<SSOCallbackResult> {
  return request('POST', `${BASE}/${provider}/callback`, {}, { ...body }, QUIET_OPTIONS).then(({ data }) => ({
    outcome: data.outcome,
    registrationStep: data.registration_step ?? null,
    provider: data.provider,
    flow: data.flow,
    returnPath: data.return_path ?? null,
    email: data.identity?.email ?? '',
    expiresInSeconds: data.expires_in ?? null,
    resendAfterSeconds: data.resend_after ?? null,
  }));
}

/**
 * Sends back the code that went to the account's address after a `link_required` callback; links the identity and
 * signs the session in.
 */
export function confirmSSOLink(code: string): Promise<SSOLinkResult> {
  return request('POST', `${BASE}/link`, {}, { code }, QUIET_OPTIONS).then(({ data }) => ({
    registrationStep: data.registration_step ?? null,
    returnPath: data.return_path ?? null,
  }));
}

/**
 * Sends a fresh link code to the account's address.
 */
export function resendSSOLinkCode(): Promise<SSOLinkCode> {
  return request('POST', `${BASE}/link/resend`, {}, {}, QUIET_OPTIONS).then(({ data }) => ({
    expiresInSeconds: data.expires_in,
    resendAfterSeconds: data.resend_after,
  }));
}
